import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Segment,
  Table,
} from "semantic-ui-react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";

const catalogosHC = [
  { key: "tipo_in", value: "tipo_in", text: "Tipos de entrada" },
  { key: "tipo_out", value: "tipo_out", text: "Tipos de salida" },
  { key: "tip_doc_in", value: "tip_doc_in", text: "Documentos entrada" },
  { key: "tip_doc_out", value: "tip_doc_out", text: "Documentos salida" },
  { key: "programas", value: "programas", text: "Programas mantención" },
];

const catalogosST = [
  { key: "medidas", value: "medidas", text: "Unidades de medida" },
  { key: "item_st", value: "item_st", text: "Categorías Ítems ST" },
  {
    key: "categoria_protocolo",
    value: "categoria_protocolo",
    text: "Categorías Ítems de Mantención",
  },
];

const limpiarId = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/\//g, "_")
    .replace(/[^\w-]/g, "");

const AdminCatalogoEmpresa = () => {
  const { users } = useContext(UserContext);

  const esHC = users?.rol === "ADMIN-HC";
  const esST = users?.rol === "ADMIN-LMST";

  const catalogosOptions = useMemo(() => {
    if (esHC) return catalogosHC;
    if (esST) return catalogosST;
    return [];
  }, [esHC, esST]);

  const dominio = useMemo(() => {
    if (esHC) {
      return {
        nombre: "HomeCare",
        descripcion: "Catálogos utilizados en los procesos operacionales de HomeCare.",
        icon: "home",
        color: "teal",
      };
    }

    if (esST) {
      return {
        nombre: "Servicio Técnico",
        descripcion: "Catálogos utilizados en los procesos del Servicio Técnico.",
        icon: "wrench",
        color: "blue",
      };
    }

    return {
      nombre: "Sin dominio",
      descripcion: "Este perfil no tiene catálogos disponibles para administrar.",
      icon: "lock",
      color: "grey",
    };
  }, [esHC, esST]);

  const [catalogoId, setCatalogoId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const [nuevo, setNuevo] = useState({
    value: "",
    text: "",
    orden: "",
  });

  useEffect(() => {
    if (catalogosOptions.length === 0) {
      setCatalogoId("");
      setItems([]);
      return;
    }

    const existe = catalogosOptions.some((item) => item.value === catalogoId);

    if (!existe) {
      setCatalogoId(catalogosOptions[0].value);
    }
  }, [catalogosOptions, catalogoId]);

  const cargarItems = async () => {
    if (!users?.emp_id || !catalogoId) {
      setItems([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const q = query(
        collection(db, "catalogosEmpresa"),
        where("emp_id", "==", users.emp_id),
        where("catalogoId", "==", catalogoId)
      );

      const snap = await getDocs(q);

      const data = snap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        .sort((a, b) => {
          const ordenA = Number(a.orden ?? 999);
          const ordenB = Number(b.orden ?? 999);

          if (ordenA !== ordenB) return ordenA - ordenB;

          return String(a.text || "").localeCompare(String(b.text || ""), "es");
        });

      setItems(data);
    } catch (err) {
      console.error("Error al cargar catálogo:", err);
      setError("Error al cargar el catálogo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (catalogoId && users?.emp_id) {
      cargarItems();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogoId, users?.emp_id]);

  const cambiarCampoLocal = (id, campo, valor) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [campo]: valor,
            }
          : item
      )
    );
  };

  const guardarItem = async (item) => {
    if (!item?.id || !item.activo) return;

    const texto = String(item.text || "").trim();

    if (!texto) {
      setError("El texto visible no puede estar vacío.");
      return;
    }

    setGuardando(true);
    setError("");
    setOk("");

    try {
      await updateDoc(doc(db, "catalogosEmpresa", item.id), {
        text: texto.toUpperCase(),
        orden: Number(item.orden) || 999,
        fechamod: serverTimestamp(),
        usermod: users?.correo || "sistema",
      });

      setOk("Ítem actualizado correctamente.");
      await cargarItems();
    } catch (err) {
      console.error("Error al actualizar ítem:", err);
      setError("Error al guardar el ítem.");
    } finally {
      setGuardando(false);
    }
  };

  const cambiarActivo = async (item) => {
    if (!item?.id) return;

    setGuardando(true);
    setError("");
    setOk("");

    try {
      const nuevoEstado = item.activo !== true;

      await updateDoc(doc(db, "catalogosEmpresa", item.id), {
        activo: nuevoEstado,
        fechamod: serverTimestamp(),
        usermod: users?.correo || "sistema",
      });

      setOk(
        nuevoEstado
          ? "Ítem activado correctamente."
          : "Ítem desactivado correctamente."
      );

      await cargarItems();
    } catch (err) {
      console.error("Error al cambiar estado:", err);
      setError("Error al cambiar el estado del ítem.");
    } finally {
      setGuardando(false);
    }
  };

  const agregarItem = async () => {
    if (!users?.emp_id || !catalogoId) {
      setError("No se pudo determinar la empresa o el catálogo.");
      return;
    }

    const texto = String(nuevo.text || "").trim();

    if (!texto) {
      setError("Debe ingresar el texto del nuevo ítem.");
      return;
    }

    setGuardando(true);
    setError("");
    setOk("");

    try {
      const valueFinal = nuevo.value.trim()
        ? nuevo.value.trim().toUpperCase()
        : texto.toUpperCase();

      const itemId = limpiarId(valueFinal);

      if (!itemId) {
        setError("No se pudo generar un identificador válido para el ítem.");
        return;
      }

      const existeQ = query(
        collection(db, "catalogosEmpresa"),
        where("emp_id", "==", users.emp_id),
        where("catalogoId", "==", catalogoId),
        where("itemId", "==", itemId)
      );

      const existeSnap = await getDocs(existeQ);

      if (!existeSnap.empty) {
        setError("Ya existe un ítem con ese valor en este catálogo.");
        return;
      }

      await addDoc(collection(db, "catalogosEmpresa"), {
        emp_id: users.emp_id,
        catalogoId,
        itemId,
        key: valueFinal,
        value: valueFinal,
        text: texto.toUpperCase(),
        orden: Number(nuevo.orden) || items.length + 1,
        activo: true,
        sistema: false,
        fechaadd: serverTimestamp(),
        useradd: users?.correo || "sistema",
        fechamod: serverTimestamp(),
        usermod: users?.correo || "sistema",
      });

      setNuevo({
        value: "",
        text: "",
        orden: "",
      });

      setOk("Ítem agregado correctamente.");
      await cargarItems();
    } catch (err) {
      console.error("Error al agregar ítem:", err);
      setError("Error al agregar el ítem.");
    } finally {
      setGuardando(false);
    }
  };

  if (catalogosOptions.length === 0) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#f8fafc",
          padding: 24,
        }}
      >
        <Segment
          style={{
            background: "#fff",
            border: "1px solid #e5e9ef",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,.04)",
          }}
        >
          <Message
            warning
            icon="lock"
            header="Catálogos no disponibles"
            content="Este perfil no tiene catálogos operacionales disponibles para administrar."
          />
        </Segment>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: 24,
      }}
    >
      <Segment
        style={{
          background: "#fff",
          border: "1px solid #e5e9ef",
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,.04)",
          marginBottom: 16,
        }}
      >
        <Header as="h2" style={{ marginBottom: 6, color: "#172b4d" }}>
          <Icon name="list alternate outline" />

          <Header.Content>
            Administración de Catálogos

            <Header.Subheader
              style={{
                marginTop: 5,
                color: "#6b778c",
              }}
            >
              Administración de parámetros configurables por empresa
            </Header.Subheader>
          </Header.Content>
        </Header>

        <Label color={dominio.color} size="large" style={{ marginTop: 10 }}>
          <Icon name={dominio.icon} />
          {dominio.nombre}
        </Label>
      </Segment>

      <Message
        info
        icon={dominio.icon}
        header={`Catálogos de ${dominio.nombre}`}
        content={dominio.descripcion}
      />

      {error && (
        <Message negative onDismiss={() => setError("")}>
          <Message.Header>No fue posible completar la operación</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {ok && (
        <Message positive onDismiss={() => setOk("")}>
          <Message.Header>Operación realizada</Message.Header>
          <p>{ok}</p>
        </Message>
      )}

      <Segment
        style={{
          background: "#fff",
          border: "1px solid #e5e9ef",
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,.04)",
          overflow: "visible",
        }}
      >
        <Header as="h4" style={{ color: "#253858" }}>
          <Icon name="folder open outline" />
          <Header.Content>Seleccionar catálogo</Header.Content>
        </Header>

        <Form>
          <Form.Field>
            <label>Catálogo</label>

            <Dropdown
              fluid
              selection
              search
              options={catalogosOptions}
              value={catalogoId}
              placeholder="Seleccione un catálogo"
              onChange={(e, { value }) => {
                setCatalogoId(value);
                setError("");
                setOk("");
              }}
            />
          </Form.Field>
        </Form>
      </Segment>

      <Segment
        style={{
          background: "#fff",
          border: "1px solid #e5e9ef",
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,.04)",
          marginTop: 16,
        }}
      >
        <Header as="h4" style={{ color: "#253858" }}>
          <Icon name="plus circle" color="teal" />

          <Header.Content>
            Agregar nuevo ítem
            <Header.Subheader>
              Agregue una nueva opción al catálogo seleccionado.
            </Header.Subheader>
          </Header.Content>
        </Header>

        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Valor</label>
              <Input
                placeholder="Ej: REPUESTO"
                value={nuevo.value}
                onChange={(e) =>
                  setNuevo((prev) => ({
                    ...prev,
                    value: e.target.value,
                  }))
                }
              />
            </Form.Field>

            <Form.Field required>
              <label>Texto visible</label>
              <Input
                placeholder="Ej: REPUESTO"
                value={nuevo.text}
                onChange={(e) =>
                  setNuevo((prev) => ({
                    ...prev,
                    text: e.target.value,
                  }))
                }
              />
            </Form.Field>

            <Form.Field>
              <label>Orden</label>
              <Input
                type="number"
                min="1"
                placeholder="Ej: 1"
                value={nuevo.orden}
                onChange={(e) =>
                  setNuevo((prev) => ({
                    ...prev,
                    orden: e.target.value,
                  }))
                }
              />
            </Form.Field>
          </Form.Group>

          <Button
            color="teal"
            type="button"
            loading={guardando}
            disabled={guardando || !catalogoId || !nuevo.text.trim()}
            onClick={agregarItem}
          >
            <Icon name="plus" />
            Agregar
          </Button>
        </Form>
      </Segment>

      <Segment
        style={{
          background: "#fff",
          border: "1px solid #e5e9ef",
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,.04)",
          marginTop: 16,
          padding: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "16px 18px",
            borderBottom: "1px solid #e5e9ef",
          }}
        >
          <Header as="h4" style={{ margin: 0, color: "#253858" }}>
            <Icon name="list" />

            <Header.Content>
              Ítems del catálogo
              <Header.Subheader>
                {items.length}{" "}
                {items.length === 1 ? "ítem registrado" : "ítems registrados"}
              </Header.Subheader>
            </Header.Content>
          </Header>
        </div>

        <div style={{ overflowX: "auto" }}>
          <Table
            celled
            compact
            selectable
            style={{
              margin: 0,
              border: 0,
              borderRadius: 0,
            }}
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={2}>Valor</Table.HeaderCell>
                <Table.HeaderCell>Texto visible</Table.HeaderCell>
                <Table.HeaderCell width={2}>Orden</Table.HeaderCell>
                <Table.HeaderCell width={2}>Origen</Table.HeaderCell>
                <Table.HeaderCell width={2}>Estado</Table.HeaderCell>
                <Table.HeaderCell width={2}>Acciones</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {loading ? (
                <Table.Row>
                  <Table.Cell colSpan="6" textAlign="center" style={{ padding: 30 }}>
                    <Icon loading name="spinner" />
                    Cargando catálogo...
                  </Table.Cell>
                </Table.Row>
              ) : items.length === 0 ? (
                <Table.Row>
                  <Table.Cell
                    colSpan="6"
                    textAlign="center"
                    style={{
                      padding: 30,
                      color: "#6b778c",
                    }}
                  >
                    <Icon name="inbox" size="large" />
                    <div style={{ marginTop: 8 }}>
                      No existen ítems registrados para este catálogo.
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : (
                items.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <strong>{item.value || "-"}</strong>
                    </Table.Cell>

                    <Table.Cell>
                      <Input
                        fluid
                        value={item.text || ""}
                        disabled={!item.activo}
                        onChange={(e) =>
                          cambiarCampoLocal(item.id, "text", e.target.value)
                        }
                      />
                    </Table.Cell>

                    <Table.Cell>
                      <Input
                        fluid
                        type="number"
                        min="1"
                        value={item.orden ?? ""}
                        disabled={!item.activo}
                        onChange={(e) =>
                          cambiarCampoLocal(item.id, "orden", e.target.value)
                        }
                      />
                    </Table.Cell>

                    <Table.Cell textAlign="center">
                      {item.sistema ? (
                        <Label size="small" color="blue">
                          Sistema
                        </Label>
                      ) : (
                        <Label size="small" basic>
                          Empresa
                        </Label>
                      )}
                    </Table.Cell>

                    <Table.Cell textAlign="center">
                      {item.activo ? (
                        <Label size="small" color="green">
                          <Icon name="check" />
                          Activo
                        </Label>
                      ) : (
                        <Label size="small" color="grey">
                          <Icon name="ban" />
                          Inactivo
                        </Label>
                      )}
                    </Table.Cell>

                    <Table.Cell textAlign="center">
                      <Button
                        icon
                        basic
                        color="blue"
                        size="small"
                        title="Guardar cambios"
                        disabled={guardando || !item.activo}
                        onClick={() => guardarItem(item)}
                      >
                        <Icon name="save" />
                      </Button>

                      <Button
                        icon
                        basic
                        color={item.activo ? "orange" : "green"}
                        size="small"
                        title={item.activo ? "Desactivar" : "Activar"}
                        disabled={guardando}
                        onClick={() => cambiarActivo(item)}
                      >
                        <Icon name={item.activo ? "ban" : "check"} />
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </div>
      </Segment>

      <Message
        style={{ marginTop: 16 }}
        icon="info circle"
        header="Conservación de registros"
        content="Los ítems que ya no se utilicen deben desactivarse en lugar de eliminarse, para conservar la compatibilidad con documentos y registros históricos."
      />
    </div>
  );
};

export default AdminCatalogoEmpresa;